import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { formatGros, formatZlatkaCompact } from "@/lib/currency";

const ACCENT = "#F0801F"; // single series - the site's own brand accent, no categorical palette needed

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-md border border-white/10 bg-[#0f0f12] px-3 py-2 text-xs text-white/80 shadow-lg">
      <div className="text-white/40">{new Date(label).toLocaleDateString("cs-CZ")}</div>
      <div className="mt-1 text-[#F0801F]">{formatGros(point.total_value_gros)}</div>
      {point.pct_change !== null && (
        <div className={Number(point.pct_change) >= 0 ? "text-emerald-400" : "text-red-400"}>
          {Number(point.pct_change) >= 0 ? "+" : ""}
          {(Number(point.pct_change) * 100).toFixed(2)} %
        </div>
      )}
      {point.source === "gm_override" && <div className="mt-1 text-white/40">Zásah GM{point.gm_note ? `: ${point.gm_note}` : ""}</div>}
    </div>
  );
};

// Single-series area chart of a company's live market value over time -
// dataviz skill's "one axis, one hue" rule for a lone series (the site's
// own orange accent, since there's no identity to distinguish by color
// here). Floor/base/ceiling render as recessive reference lines, not marks.
export const MarketHistoryChart = ({ history, baseValueGros, floorGros, ceilingGros }) => {
  if (!history || history.length === 0) {
    return (
      <p className="text-sm text-white/50">
        Trh se pro tenhle podnik ještě nehýbal - první záznam přibude po nejbližší denní simulaci.
      </p>
    );
  }

  const data = history.map((h) => ({
    date: h.recorded_at,
    total_value_gros: h.total_value_gros,
    pct_change: h.pct_change,
    source: h.source,
    gm_note: h.gm_note,
  }));

  return (
    <div style={{ width: "100%", height: 260 }}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="marketFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={ACCENT} stopOpacity={0.35} />
              <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#2c2c2a" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={(d) => new Date(d).toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric" })}
            stroke="#383835"
            tick={{ fill: "#898781", fontSize: 11 }}
          />
          <YAxis
            tickFormatter={formatZlatkaCompact}
            stroke="#383835"
            tick={{ fill: "#898781", fontSize: 11 }}
            width={70}
            domain={["auto", "auto"]}
          />
          <ReferenceLine y={baseValueGros} stroke="#c3c2b7" strokeDasharray="4 4" />
          <ReferenceLine y={floorGros} stroke="#e66767" strokeOpacity={0.5} strokeDasharray="2 4" />
          <ReferenceLine y={ceilingGros} stroke="#199e70" strokeOpacity={0.5} strokeDasharray="2 4" />
          <Tooltip content={<CustomTooltip />} />
          <Area type="monotone" dataKey="total_value_gros" stroke={ACCENT} strokeWidth={2} fill="url(#marketFill)" />
        </AreaChart>
      </ResponsiveContainer>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-white/40">
        <span>- - - základní hodnota</span>
        <span className="text-red-400/70">- - - podlaha (20 %)</span>
        <span className="text-emerald-400/70">- - - strop (300 %)</span>
      </div>
    </div>
  );
};
